import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import { downloadEudamedXmlFile, sendEudamedXml, EudamedPlaygroundNotice, describeDownloadWarnings } from "../Shared/EudamedXmlDownload.js";
import ProductPackageDI from "../Shared/FieldTypes/ProductPackageDI.js";
import ClinicallyRelevantSizes from "../Shared/FieldTypes/ClinicallyRelevantSizes.js";
import StorageAndHandling from "../Shared/FieldTypes/StorageAndHandling.js";
import ProductionIdentifiers from "../Shared/FieldTypes/ProductionIdentifiers.js";
import EMDNNomenclature from "../Shared/FieldTypes/EMDNNomenclature.js";
import AdditionalProductDescription from "../Shared/FieldTypes/AdditionalProductDescription.js";
import TradeName from "../Shared/FieldTypes/TradeName.js";
import NonMedicalDeviceType from "../Shared/FieldTypes/NonMedicalDeviceType.js";
import CMRSubstanceList from "../Shared/FieldTypes/CMRSubstanceList.js";
import EndocrineSubstancesList from "../Shared/FieldTypes/EndocrineSubstancesList.js";
import MedicinalProductSubstancesList from "../Shared/FieldTypes/MedicinalProductSubstancesList.js";
import ClinicalSizes from "../Shared/FieldTypes/ClinicalSizes.js";
import MarketInfo from "../Shared/FieldTypes/MarketInfo.js";
import ContainerPack from "../Shared/FieldTypes/ContainerPack.js";
import ProductDesignerLink from "../Shared/FieldTypes/ProductDesignerLink.js";
import EUStorageAndHandlingConditionsList, { shcTypes, findMissingRequiredComment } from "../Shared/FieldTypes/EUStorageAndHandlingConditionsList.js";
import CriticalWarningsOrContraindications, { findInvalidOtherWarnings, otherWarningError } from "../Shared/FieldTypes/CriticalWarningsOrContraindications.js";
import CatalogNumber from "../Shared/FieldTypes/CatalogNumber.js";
import IssuingAgency from "../Shared/FieldTypes/IssuingAgency.js";
import SecondaryIssuingAgency from "../Shared/FieldTypes/SecondaryIssuingAgency.js";
import PreviousIssuingAgency from "../Shared/FieldTypes/PreviousIssuingAgency.js";
import GenericYesNo from "../Shared/FieldTypes/GenericYesNo.js";
import WhatMRISafetyInformationDoesTheLabelingContain from "../Shared/FieldTypes/WhatMRISafetyInformationDoesTheLabelingContain.js";
import SterilizationMethod from "../Shared/FieldTypes/SterilizationMethod.js";
import UDIDateField from "../Shared/FieldTypes/UDIDateField.js";
import PrimaryDINumber from "../Shared/FieldTypes/PrimaryDINumber.js";
import DeviceCount from "../Shared/FieldTypes/DeviceCount.js";
import UDITextField from "../Shared/FieldTypes/UDITextField.js";
import MasterUDIDICode from "../Shared/FieldTypes/MasterUDIDICode.js";
import IssuingEntityMasterUDIDI from "../Shared/FieldTypes/IssuingEntityMasterUDIDI.js";
import IssuingEntitySecondaryMasterUDIDI from "../Shared/FieldTypes/IssuingEntitySecondaryMasterUDIDI.js";
import IssuingEntityUnitofUseDI from "../Shared/FieldTypes/IssuingEntityUnitofUseDI.js";
import IssuingEntityDirectMarkingDI from "../Shared/FieldTypes/IssuingEntityDirectMarkingDI.js";
import ReferenceCatalogueNumber from "../Shared/FieldTypes/ReferenceCatalogueNumber.js";
import UDIDIDeviceStatus from "../Shared/FieldTypes/UDIDIDeviceStatus.js";
import MemberStateOfThePlacingOnTheEUMarketOfTheDevice from "../Shared/FieldTypes/MemberStateOfThePlacingOnTheEUMarketOfTheDevice.js";
import RelationshipType from "../Shared/FieldTypes/RelationshipType.js";
import BasicUDIDIIdentifier from "../Shared/FieldTypes/BasicUDIDIIdentifier.js";
import MaximumNumberOfReuses from "../Shared/FieldTypes/MaximumNumberOfReuses.js";
import UnitOfUseDiNumber from "../Shared/FieldTypes/UnitOfUseDiNumber.js";
import { euUdiMajorFor, parseEuUdiVersion } from "./EuUdiVersion.js";


const ProductUDIs = ({ AppContext, initialTab = null, initialVersionId = null }) => {

    const [visibleDetailsTab, setVisibleDetailsTab] = useState(initialTab === "eu" ? "eu" : "us");
    const [isEditUSUDIMode, setIsEditUSUDIMode] = useState(false);
    const [isEditEUUDIMode, setIsEditEUUDIMode] = useState(false);

    const [isSavingUSUDIs, setIsSavingUSUDIs] = useState(false);
    const [isSavingEUUDIs, setIsSavingEUUDIs] = useState(false);
    const [isShowingUSUDISaveMessage, setIsShowingUSUDISaveMessage] = useState(false);
    const [isShowingEUUDISaveMessage, setIsShowingEUUDISaveMessage] = useState(false);

    const [isShowingUSUDIErrorMessage, setIsShowingUSUDIErrorMessage] = useState(false);
    const [isShowingEUUDIErrorMessage, setIsShowingEUUDIErrorMessage] = useState(false);

    const [uSUDIversion, setUSUDIVersion] = useState(0);
    const [eUUDIversion, setEUUDIVersion] = useState(0);

    const [USUDI, setUSUDI] = useState(null);
    const [EUUDI, setEUUDI] = useState(null);
    const [originalUSUDI, setOriginalUSUDI] = useState(null);
    const [originalEUUDI, setOriginalEUUDI] = useState(null);
    const [isLoadingUSUDIs, setIsLoadingUSUDIs] = useState(true);
    const [isLoadingEUUDIs, setIsLoadingEUUDIs] = useState(true);
    const [hasUSUDIs, setHasUSUDIs] = useState(false);
    const [hasEUUDIs, setHasEUUDIs] = useState(false);

    // EU UDI versions: the shown version and the product's version list (biggest first)
    const [euUdiVersionInfo, setEUUDIVersionInfo] = useState(null);
    const [euUdiVersions, setEUUDIVersions] = useState([]);
    const [basicUdiRegulations, setBasicUdiRegulations] = useState({});
    const [euUdiSaveErrorMessage, setEUUDISaveErrorMessage] = useState(null);

    const [error, setError] = useState(false);
    // Incrementing counters (not booleans) so a repeated failed Save always re-fires the child's
    // effect, even if the previous value was already truthy and React batches the reset+set together.
    const [storageValidationTrigger, setStorageValidationTrigger] = useState(0);
    const [warningsValidationTrigger, setWarningsValidationTrigger] = useState(0);
    const [productDesignerValidationTrigger, setProductDesignerValidationTrigger] = useState(0);

    const [showEudamedXmlPopup, setShowEudamedXmlPopup] = useState(false);
    const [selectedEudamedService, setSelectedEudamedService] = useState("UDI_DI_UPDATE");
    const [isGeneratingEudamedXml, setIsGeneratingEudamedXml] = useState(false);
    const [eudamedXmlError, setEudamedXmlError] = useState(null);
    const [eudamedXmlWarning, setEudamedXmlWarning] = useState(null);
    const [isSendingEudamedXml, setIsSendingEudamedXml] = useState(false);
    const [eudamedSendSuccess, setEudamedSendSuccess] = useState(null);
    const eudamedEnabled = window.enableEUDAMED === true;

    // EUDAMED XML exports for THIS product's EU (Master) UDI-DI - same productIds-keyed endpoints
    // as the Products page bulk export popup, called with just the current product's id.
    // filename gets the product SKU appended (when set) so downloads are identifiable per product.
    const eudamedXmlServices = [
        {
            value: "UDI_DI_UPDATE",
            label: "Update (Master) UDI-DI",
            url: "/umbraco/api/RIMSProducts/GenerateEUUDIUpdateXml/?productIds=",
            filename: "UDIDIUpdate",
            extension: ".xml",
        },
        {
            value: "UDI_DI_REGISTER",
            label: "Register new (Master) UDI-DI under existing Basic UDI-DI",
            url: "/umbraco/api/RIMSProducts/GenerateAdditionalEUUDIXml/?productId=",
            filename: "AdditionalUDIDI",
            extension: ".xml",
        },
        {
            value: "MARKET_INFO_UPDATE",
            label: "Update Market Information (countries)",
            url: "/umbraco/api/RIMSProducts/GenerateMarketInfoUpdateXml/?productIds=",
            filename: "MarketInfoUpdate",
            extension: ".xml",
        },
        {
            value: "PACKAGE_UDI_UPDATE",
            label: "Update Container Packages",
            url: "/umbraco/api/RIMSProducts/GeneratePackageUdiUpdateXml/?productIds=",
            filename: "PackageUDIUpdate",
            extension: ".xml",
        },
        {
            value: "PRODUCT_DESIGNER_UPDATE",
            label: "Update Product Designer / original manufacturer",
            url: "/umbraco/api/RIMSProducts/GenerateProductDesignerUpdateXml/?productIds=",
            filename: "ProductDesignerUpdate",
            extension: ".xml",
        },
    ];

    // XML of the shown version, not just the product's current one
    const eudamedXmlUrl = (service) => {
        const euUdiId = euUdiVersionInfo?.EUUDIId;
        const versionParam = euUdiId ? (service.value === "UDI_DI_REGISTER" ? "&euUdiId=" : "&euUdiIds=") + euUdiId : "";
        return service.url + AppContext.currentItem.Id + versionParam;
    };

    const openEudamedXmlPopup = () => {
        setSelectedEudamedService("UDI_DI_UPDATE");
        setEudamedXmlError(null);
        setEudamedXmlWarning(null);
        setEudamedSendSuccess(null);
        setShowEudamedXmlPopup(true);
    };

    const downloadEudamedXml = () => {

        const service = eudamedXmlServices.find(s => s.value === selectedEudamedService);
        if (!service)
            return;

        const sku = AppContext.currentItem.SKUProductCode;
        const filename = service.filename + (sku ? `_${sku}` : "") + service.extension;

        setIsGeneratingEudamedXml(true);
        setEudamedXmlError(null);
        setEudamedXmlWarning(null);

        downloadEudamedXmlFile(eudamedXmlUrl(service), filename)
            .then((result) => {
                const warning = describeDownloadWarnings(result);
                if (warning)
                    setEudamedXmlWarning(warning);
                else
                    setShowEudamedXmlPopup(false);
            })
            .catch((err) => {
                setEudamedXmlError(err.message);
            })
            .finally(() => {
                setIsGeneratingEudamedXml(false);
            });
    };


    // Generates the selected service's XML server-side and submits it straight to EUDAMED
    // (SubmitEudamedXml re-validates the company SRN and the module's security key and rejects
    // with an actionable message when either is missing). Blocked unless the company's
    // enableEUDAMED flag is on; not available for zip-producing services.
    const sendEudamedXmlToEudamed = () => {

        if (!eudamedEnabled)
            return;

        const service = eudamedXmlServices.find(s => s.value === selectedEudamedService);
        if (!service || service.extension === ".zip")
            return;

        setIsSendingEudamedXml(true);
        setEudamedXmlError(null);
        setEudamedXmlWarning(null);
        setEudamedSendSuccess(null);

        sendEudamedXml(eudamedXmlUrl(service))
            .then(() => {
                setEudamedSendSuccess("Sent to EUDAMED successfully.");
            })
            .catch((err) => {
                setEudamedXmlError(err.message);
            })
            .finally(() => {
                setIsSendingEudamedXml(false);
            });
    };



    const getUSUDIs = () => {

        setIsLoadingUSUDIs(true);

        let dataUrl = "/umbraco/api/RIMSProducts/USUDI/?productId=" + AppContext.currentItem.Id;

        let requestOptions = {
            method: "GET",
        };

        fetch(dataUrl, requestOptions)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
            .then((result) => {

                setUSUDI(result.USUDI);
                setOriginalUSUDI(result.USUDI);
                
                setHasUSUDIs(true);

            })
            .catch((error) => {
                // Handle any errors that occurred during the fetch
                console.log("error: " + error);
            })
            .finally(() => {
                setIsLoadingUSUDIs(false);
            });

    }


    
    // Loads one EU UDI version, the current one when euUdiId is empty
    const getEUUDIs = (euUdiId = null) => {

        setIsLoadingEUUDIs(true);

        let dataUrl = "/umbraco/api/RIMSProducts/EUUDI/?productId=" + AppContext.currentItem.Id;
        if (euUdiId)
            dataUrl += "&euUdiId=" + encodeURIComponent(euUdiId);

        let requestOptions = {
            method: "GET",
        };

        fetch(dataUrl, requestOptions)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
            .then((result) => {

                setEUUDI(result.EUUDI);
                setOriginalEUUDI(result.EUUDI);
                setEUUDIVersionInfo(result.EUUDIId ? result : null);
                setEUUDIVersion(v => v + 1);

                setHasEUUDIs(true);

            })
            .catch((error) => {
                // Handle any errors that occurred during the fetch
                console.log("error: " + error);
            })
            .finally(() => {
                setIsLoadingEUUDIs(false);
            });

        getEUUDIVersions();
    }


    const getEUUDIVersions = () => {

        fetch("/umbraco/api/RIMSProducts/EUUDIVersions/?productId=" + AppContext.currentItem.Id)
            .then((response) => {
                if (!response.ok)
                    throw new Error("Network response was not ok");
                return response.json();
            })
            .then((result) => setEUUDIVersions(result))
            .catch((error) => console.log("error: " + error));
    }


    // Regulation of each Basic UDI: it decides the version major of an edit
    const getBasicUdiRegulations = () => {

        fetch("/umbraco/api/RIMSProducts/BasicUDIs")
            .then((response) => {
                if (!response.ok)
                    throw new Error("Network response was not ok");
                return response.json();
            })
            .then((result) => setBasicUdiRegulations(Object.fromEntries(result.map(b => [String(b.Id), b.ApplicableRegulation]))))
            .catch((error) => console.log("error: " + error));
    }



    useEffect(() => {

        if (visibleDetailsTab == "us" && !hasUSUDIs)
            getUSUDIs();

        if (visibleDetailsTab == "eu" && !hasEUUDIs) {
            getEUUDIs(initialVersionId);
            getBasicUdiRegulations();
        }


    }, [visibleDetailsTab]);



    
    const isNullOrEmpty = (val) => {
        return val === "" || val === null;
    }

    const hasValue = (val) => {
        return val !== "" && val !== null;
    }
        

    
    const validFormField = (title, field) => {


        if (field == "USUDI.DeviceCount") {

            let value = USUDI.DeviceCount;

            if (isNullOrEmpty(value)) {
                setError(title + " is a mandatory field"); return false
            }

            if (hasValue(value)) {
                if (isNaN(value)) {
                    setError(title + " must be numeric"); return false;
                }
                if (value < 0 || value > 9999999) {
                    setError(title + " must be between 0 and 9999999"); return false;
                }
            }
        }

        if (field == "USUDI.UnitOfUseDINumber") {

            let value = USUDI.UnitOfUseDINumber;
            let auxValue = USUDI.DeviceCount;


            if (isNullOrEmpty(value) && auxValue > 0) {
                setError(title + " cannot be empty when 'Device Count' is greater than 0"); return false;
            }


            if (hasValue(value)) {
                if (value.length < 6 || value.length > 23) {
                    setError(title + " length must be between 6 and 23"); return false;
                }
            }
        }

        if (field == "USUDI.DM_DI_Number") {

            let value = USUDI.DM_DI_Number;

            if (hasValue(value)) {
                if (value.length < 6 || value.length > 23) {
                    setError(title + " length must be between 6 and 23"); return false;
                }
            }
        }

        if (field == "USUDI.SecondaryDINumber") {

            let value = USUDI.SecondaryDINumber;

            if (hasValue(value)) {
                if (value.length < 6 || value.length > 23) {
                    setError(title + " length must be between 6 and 23"); return false;
                }
            }
        }

        if (field == "USUDI.PreviousDINumber") {

            let value = USUDI.PreviousDINumber;

            if (hasValue(value)) {
                if (value.length < 6 || value.length > 23) {
                    setError(title + " length must be between 6 and 23"); return false;
                }
            }
        }

        if (field == "USUDI.Code") {

            let value = USUDI.Code;

            if (hasValue(value)) {
                if (isNaN(value)) {
                    setError(title + " must be numeric"); return false;
                }
                if (value < 0 || value > 99999) {
                    setError(title + " must be between 0 and 99999"); return false;
                }
            }
        }

        if (field == "EUUDI.Master_UDI_DI_Code") {
            // The server saves the product's UDI-DI here, so check that instead of the stored copy
            if (isNullOrEmpty(AppContext.currentItem.UDIDI ?? null)) {
                setError(title + " is mandatory"); return false;
            }
        }

        if (field == "EUUDI.IssuingEntityMaster_UDI_DI") {
            if (isNullOrEmpty(EUUDI.IssuingEntityMaster_UDI_DI)) {
                setError(title + " is mandatory"); return false;
            }
        }

        if (field == "EUUDI.LabelledAsSingleUse") {
            if (isNullOrEmpty(EUUDI.LabelledAsSingleUse)) {
                setError(title + " is mandatory"); return false;
            }
        }

        if (field == "EUUDI.DeviceLabelledSterile") {
            if (isNullOrEmpty(EUUDI.DeviceLabelledSterile)) {
                setError(title + " is mandatory"); return false;
            }
        }

        if (field == "EUUDI.NeedForSterilisationBeforeUse") {
            if (isNullOrEmpty(EUUDI.NeedForSterilisationBeforeUse)) {
                setError(title + " is mandatory"); return false;
            }
        }

        if (field == "EUUDI.TradeNameApplicable") {
            if (isNullOrEmpty(EUUDI.TradeNameApplicable)) {
                setError(title + " is mandatory"); return false;
            }
        }

        if (field == "EUUDI.UDI_DI_DeviceStatus") {
            if (isNullOrEmpty(EUUDI.UDI_DI_DeviceStatus)) {
                setError(title + " is mandatory"); return false;
            }
        }

        if (field == "EUUDI.QuantityOfDevice") {

            let value = EUUDI.QuantityOfDevice;

            if (isNullOrEmpty(value)) {
                setError(title + " is mandatory"); return false;
            }

            if (hasValue(value)) {
                if (isNaN(value)) {
                    setError(title + " must be numeric"); return false;
                }
                if (value < 1 || value > 99999) {
                    setError(title + " must be between 1 and 99999"); return false;
                }
            }
        }



        if (field == "EUUDI.MaximumNumberOfReuses") {

            let value = EUUDI.MaximumNumberOfReuses;
            let auxValue = EUUDI.LabelledAsSingleUse;

            if (value === undefined || isNullOrEmpty(value)) {
                setError(title + " is mandatory"); return false;
            }

            if (isNaN(value)) {
                setError(title + " must be numeric"); return false;
            }

            if (String(auxValue) === "true" && Number(value) !== 0) {
                setError(title + " must be 0 when 'Labelled as Single Use' is set to 'Yes'"); return false;
            }

            if (String(auxValue) === "false" && (isNullOrEmpty(value) || !Number.isInteger(Number(value)) || (Number(value) <= 0 && Number(value) !== -1))) {
                setError(title + " must be a positive number, or -1 if not defined, when 'Labelled as Single Use' is set to 'No'"); return false;
            }
        }


        if (field == "EUUDI.RelationshipType") {

            let value = EUUDI.RelationshipType;
            let auxValue = EUUDI.RelatedLegacyDevice;

            if (isNullOrEmpty(value) && !isNullOrEmpty(auxValue)) {
                setError(title + " cannot be empty when a Related Legacy Device is specified"); return false;
            }
            
        }







        
        if (field == "EUUDI.NomenclatureCode") {
            const list = EUUDI.NomenclatureCode;
            if (!list || list.length === 0) {
                setError(title + " is mandatory"); return false;
            }
        }
        if (field == "EUUDI.ListOfStorageAndHandlingConditions") {
            const missing = findMissingRequiredComment(EUUDI.ListOfStorageAndHandlingConditions);
            if (missing.length > 0) {
                const match = shcTypes.find(t => t.key === missing[0].Type?.Value);
                const label = match ? `${match.value} - ${match.label}` : (missing[0].Type?.Text || "");
                const guidance = match?.value === "SHC099" ? "with free text explaining the condition" : "containing that value";
                setError(title + ` - '${label}' requires a Description ${guidance}.`); return false;
            }
        }
        if (field == "EUUDI.ListOfCriticalWarningsOrContraindications") {
            const invalid = findInvalidOtherWarnings(EUUDI.ListOfCriticalWarningsOrContraindications);
            if (invalid.length > 0) {
                setError(title + " - " + otherWarningError(invalid[0].Descriptions)); return false;
            }
        }
        if (field == "EUUDI.NaturalOrLegalPersonWhoManufacturedAndDesingedTheDevice") {
            const designer = EUUDI.NaturalOrLegalPersonWhoManufacturedAndDesingedTheDevice;
            if (!designer)
                return true;

            const hasSrn = designer.SRN != null && String(designer.SRN).trim() !== "";
            const address = designer.GeographicalAddress;
            const isObjectAddress = address && typeof address === "object";
            const countryValue = isObjectAddress ? (address.Country?.Value ?? address.Country?.value ?? "") : "";
            const postCode = isObjectAddress ? (address.PostCode ?? address.postCode ?? "") : "";
            const city = isObjectAddress ? (address.City ?? address.city ?? "") : "";
            const orgName = designer.OrganizationName ?? designer.organizationName ?? "";
            const languageValue = designer.Language?.Value ?? designer.Language?.value ?? "";

            const normalizeContactRow = (raw) => {
                if (raw == null || raw === "") return { EMail: "", Phone: "", Public: "", Website: "" };
                if (typeof raw === "string") return { EMail: raw, Phone: "", Public: "", Website: "" };
                const pub = raw.Public ?? raw.public;
                return {
                    EMail: raw.EMail ?? raw.eMail ?? raw.Email ?? raw.email ?? "",
                    Phone: raw.Phone ?? raw.phone ?? "",
                    // Tri-state: true/false is an explicit answer, "" means never answered.
                    Public: pub === true || pub === "true" ? true : (pub === false || pub === "false" ? false : ""),
                    Website: raw.Website ?? raw.website ?? "",
                };
            };

            const normalizeContactList = (raw) => {
                if (raw == null || raw === "") return [];
                if (typeof raw === "string") {
                    const legacy = String(raw).trim();
                    return legacy ? [normalizeContactRow(legacy)] : [];
                }
                if (typeof raw !== "object") return [];
                const detail = raw.ContactDetail ?? raw.contactDetail;
                if (Array.isArray(detail)) return detail.map(normalizeContactRow);
                if (raw.EMail != null || raw.eMail != null || raw.Phone != null || raw.phone != null || raw.Website != null || raw.website != null)
                    return [normalizeContactRow(raw)];
                return [];
            };

            const contactRowHasContent = (c) =>
                !!(String(c.EMail || "").trim() || String(c.Phone || "").trim() || String(c.Website || "").trim());

            const isValidEmailFormat = (value) =>
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

            const contacts = normalizeContactList(designer.ContactDetails);
            const usableContacts = contacts.filter(contactRowHasContent);

            const hasAnyContent = hasSrn
                || !isNullOrEmpty(orgName)
                || !isNullOrEmpty(languageValue)
                || !isNullOrEmpty(countryValue)
                || !isNullOrEmpty(postCode)
                || !isNullOrEmpty(city)
                || !isNullOrEmpty(isObjectAddress ? address.AddressComplement : "")
                || !isNullOrEmpty(isObjectAddress ? address.POBox : "")
                || !isNullOrEmpty(isObjectAddress ? address.Street : "")
                || !isNullOrEmpty(isObjectAddress ? address.StreetNum : "")
                || !isNullOrEmpty(isObjectAddress ? (address.GPSCoordinates?.Latitude?.Value ?? address.GPSCoordinates?.Latitude ?? "") : "")
                || !isNullOrEmpty(isObjectAddress ? (address.GPSCoordinates?.Longitude?.Value ?? address.GPSCoordinates?.Longitude ?? "") : "")
                || usableContacts.length > 0;

            // The whole section is optional - if it was left untouched, there is nothing to validate.
            if (!hasAnyContent)
                return true;

            if (!hasSrn) {
                if (isNullOrEmpty(countryValue)) {
                    setError(title + " - Country is mandatory when SRN is not provided"); return false;
                }
                if (isNullOrEmpty(postCode)) {
                    setError(title + " - Post Code is mandatory when SRN is not provided"); return false;
                }
                if (isNullOrEmpty(city)) {
                    setError(title + " - City is mandatory when SRN is not provided"); return false;
                }
                if (isNullOrEmpty(orgName)) {
                    setError(title + " - Organization Name is mandatory when SRN is not provided"); return false;
                }
                if (usableContacts.length === 0) {
                    setError(title + " - At least one Contact Detail is required when SRN is not provided"); return false;
                }
            }

            if (isObjectAddress) {
                const lat = address.GPSCoordinates?.Latitude?.Value ?? address.GPSCoordinates?.Latitude;
                const lng = address.GPSCoordinates?.Longitude?.Value ?? address.GPSCoordinates?.Longitude;
                const hasLat = lat !== "" && lat != null;
                const hasLng = lng !== "" && lng != null;
                if (hasLat !== hasLng) {
                    setError(title + " - Both GPS Latitude and Longitude are required when GPS coordinates are provided"); return false;
                }
            }

            if (!hasSrn) {
                for (let i = 0; i < contacts.length; i++) {
                    const c = contacts[i];
                    if (!contactRowHasContent(c)) continue;
                    if (isNullOrEmpty(c.EMail)) {
                        setError(title + " - Contact Details #" + (i + 1) + ": Email is required when SRN is not provided"); return false;
                    }
                    if (!isValidEmailFormat(c.EMail)) {
                        setError(title + " - Contact Details #" + (i + 1) + ": Email must be a valid email address"); return false;
                    }
                    if (c.Public !== true && c.Public !== false) {
                        setError(title + " - Contact Details #" + (i + 1) + ": Public is required when SRN is not provided"); return false;
                    }
                }
            } else {
                for (let i = 0; i < contacts.length; i++) {
                    const c = contacts[i];
                    if (!contactRowHasContent(c)) continue;
                    if (isNullOrEmpty(c.EMail)) {
                        setError(title + " - Contact Details #" + (i + 1) + ": Email is required when other contact fields are provided"); return false;
                    }
                    if (!isValidEmailFormat(c.EMail)) {
                        setError(title + " - Contact Details #" + (i + 1) + ": Email must be a valid email address"); return false;
                    }
                    if (c.Public !== true && c.Public !== false) {
                        setError(title + " - Contact Details #" + (i + 1) + ": Public is required"); return false;
                    }
                }
            }
        }

        return true;

    }







    const saveUSUDIs = () => {



        let isValidForm = true;

        setError(false);

        if (!validFormField("UDI - Device Count", "USUDI.DeviceCount")) isValidForm = false;
        if (!validFormField("UDI - Unit Of Use DI Number", "USUDI.UnitOfUseDINumber")) isValidForm = false;
        if (!validFormField("UDI - DM DI Number", "USUDI.DM_DI_Number")) isValidForm = false;
        if (!validFormField("UDI - Secondary DI Number", "USUDI.SecondaryDINumber")) isValidForm = false;
        if (!validFormField("UDI - Previous DI Number", "USUDI.PreviousDINumber")) isValidForm = false;
        if (!validFormField("UDI - GMDN Code", "USUDI.Code")) isValidForm = false;

        if (!isValidForm) return false;


        setIsShowingUSUDIErrorMessage(false);
        setIsSavingUSUDIs(true);

        let dataUrl = "/umbraco/api/RIMSProducts/USUDI/?productId=" + AppContext.currentItem.Id;

        let data = {
            ProductId: AppContext.currentItem.Id,
            USUDI: USUDI
        }

        let requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data)
        };

        fetch(dataUrl, requestOptions)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
            .then((result) => {

                setUSUDI(result.USUDI);
                setOriginalUSUDI(result.USUDI);

                setIsEditUSUDIMode(false);

                setIsShowingUSUDISaveMessage(true);

            })
            .catch((error) => {
                // Handle any errors that occurred during the fetch
                console.log("error: " + error);
                setIsShowingUSUDIErrorMessage(true);
            })
            .finally(() => {
                setIsSavingUSUDIs(false);
            });


    }



    // Updates the shown version, or saves the changes as a new version of the product
    const saveEUUDIs = (saveAsNewVersion) => {


        let isValidForm = true;

        setError(false);

        if (!validFormField("UDI - Master UDI-DI Code", "EUUDI.Master_UDI_DI_Code")) isValidForm = false;
        if (!validFormField("UDI - Issuing Entity Master UDI-DI", "EUUDI.IssuingEntityMaster_UDI_DI")) isValidForm = false;
        if (!validFormField("UDI - Labelled as Single Use", "EUUDI.LabelledAsSingleUse")) isValidForm = false;
        if (!validFormField("UDI - Device Labelled Sterile", "EUUDI.DeviceLabelledSterile")) isValidForm = false;
        if (!validFormField("UDI - Need for Sterilisation Before Use", "EUUDI.NeedForSterilisationBeforeUse")) isValidForm = false;
        if (!validFormField("UDI - Trade Name Applicable", "EUUDI.TradeNameApplicable")) isValidForm = false;
        if (!validFormField("UDI - UDI-DI Device Status", "EUUDI.UDI_DI_DeviceStatus")) isValidForm = false;
        if (!validFormField("UDI - Quantity of Device", "EUUDI.QuantityOfDevice")) isValidForm = false;
        if (!validFormField("UDI - Nomenclature Code", "EUUDI.NomenclatureCode")) isValidForm = false;
        if (!validFormField("UDI - Storage and Handling Conditions", "EUUDI.ListOfStorageAndHandlingConditions")) { isValidForm = false; setStorageValidationTrigger(t => t + 1); }
        if (!validFormField("UDI - Critical Warnings or Contraindications", "EUUDI.ListOfCriticalWarningsOrContraindications")) { isValidForm = false; setWarningsValidationTrigger(t => t + 1); }
        if (!validFormField("UDI - Maximum Number Of Reuses", "EUUDI.MaximumNumberOfReuses")) isValidForm = false;
        if (!validFormField("UDI - Relationship Type", "EUUDI.RelationshipType")) isValidForm = false;
        if (!validFormField("UDI - Manufacturer / Product Designer", "EUUDI.NaturalOrLegalPersonWhoManufacturedAndDesingedTheDevice")) { isValidForm = false; setProductDesignerValidationTrigger(t => t + 1); }


        if (!isValidForm) return false;

        setIsShowingEUUDIErrorMessage(false);
        setEUUDISaveErrorMessage(null);
        setIsSavingEUUDIs(true);

        let dataUrl = "/umbraco/api/RIMSProducts/EUUDI/?productId=" + AppContext.currentItem.Id;

        let data = {
            ProductId: AppContext.currentItem.Id,
            EUUDI: EUUDI,
            EUUDIId: euUdiVersionInfo?.EUUDIId ?? null,
            SaveAsNewVersion: saveAsNewVersion === true,
        }

        let requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data)
        };

        fetch(dataUrl, requestOptions)
            .then((response) => {
                if (!response.ok) {
                    return response.text().then((text) => {
                        throw new Error(response.status === 400 && text ? text.replace(/^"|"$/g, "") : "");
                    });
                }
                return response.json();
            })
            .then((result) => {

                setEUUDI(result.EUUDI);
                setOriginalEUUDI(result.EUUDI);
                setEUUDIVersionInfo(result.EUUDIId ? result : null);
                getEUUDIVersions();

                setIsEditEUUDIMode(false);

                setIsShowingEUUDISaveMessage(true);

            })
            .catch((error) => {
                // Handle any errors that occurred during the fetch
                console.log("error: " + error);
                setEUUDISaveErrorMessage(error.message || null);
                setIsShowingEUUDIErrorMessage(true);
            })
            .finally(() => {
                setIsSavingEUUDIs(false);
            });

    }




    const changeUSUDIEditMode = (mode) => {

        setIsEditUSUDIMode(mode);

        if (mode == true)
            setIsShowingUSUDISaveMessage(false);

    }


    const changeEUUDIEditMode = (mode) => {

        setIsEditEUUDIMode(mode);

        if (mode == true)
            setIsShowingEUUDISaveMessage(false);

    }




    // Canceling USUDI edit changes
    const cancelUSUDIEdit = () => {

        setUSUDI(originalUSUDI);
        setIsEditUSUDIMode(false);
        setUSUDIVersion(v => v + 1);

    }


    // Canceling EUUDI edit changes
    const cancelEUUDIEdit = () => {

        setEUUDI(originalEUUDI);
        setIsEditEUUDIMode(false);
        setEUUDIVersion(v => v + 1);

    }


    const changeTab = (tab) => {


        if (AppContext.hasUDIUnsavedChanges) {
            AppContext.setIsShowingUDIUnsavedChangesWarning(true);
            return;
        }


        if(tab == "us")
            cancelEUUDIEdit();

        if(tab == "eu")
            cancelUSUDIEdit();

        setVisibleDetailsTab(tab);
        setError(false);

    }



    const deepEqual = (a, b) => {
        if (a === b) return true;

        if (
            typeof a !== "object" || a === null ||
            typeof b !== "object" || b === null
        ) {
            return false;
        }

        const keysA = Object.keys(a);
        const keysB = Object.keys(b);

        if (keysA.length !== keysB.length) return false;

        for (let key of keysA) {
            if (!keysB.includes(key)) return false;

            if (!deepEqual(a[key], b[key])) return false;
        }

        return true;
    };



    // Avoid losing unsaved changes

    useEffect(() => {

        if (!deepEqual(USUDI, originalUSUDI))
            AppContext.setHasUDIUnsavedChanges(true);
        else
            AppContext.setHasUDIUnsavedChanges(false);

    }, [USUDI, originalUSUDI]);


    useEffect(() => {

        if (!deepEqual(EUUDI, originalEUUDI))
            AppContext.setHasUDIUnsavedChanges(true);
        else
            AppContext.setHasUDIUnsavedChanges(false);

    }, [EUUDI, originalEUUDI]);



    const formatEUUDIVersionCreation = (createdOn, createdByName) => {
        const date = createdOn ? new Date(createdOn).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : null;
        return [date, createdByName].filter(Boolean).join(" · ");
    };

    const hasEUUDIChanges = !deepEqual(EUUDI, originalEUUDI);

    // The version major is its regulation: linking a Basic UDI with another regulation needs a new version
    const shownEUUDIVersion = parseEuUdiVersion(euUdiVersionInfo?.Version);
    const linkedBasicUdiId = EUUDI?.BasicUDI_DI_Identifier ? String(EUUDI.BasicUDI_DI_Identifier) : null;
    const editChangesRegulation = shownEUUDIVersion != null
        && (linkedBasicUdiId == null || linkedBasicUdiId in basicUdiRegulations)
        && shownEUUDIVersion.major !== euUdiMajorFor(linkedBasicUdiId ? basicUdiRegulations[linkedBasicUdiId] : null);

    const euUdiSaveButtonStyle = {
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 14px",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        whiteSpace: "nowrap",
        outline: "none",
        color: "#ffffff",
        border: "1px solid #1a73e8",
        backgroundColor: "#1a73e8",
    };

    const disabledSaveStyle = { opacity: 0.5, cursor: "not-allowed" };
    const canUpdateEUUDIVersion = hasEUUDIChanges && !editChangesRegulation;

    const renderEUUDISaveButtons = () => euUdiVersionInfo ? <>
        <button
            onClick={() => saveEUUDIs(false)}
            disabled={!canUpdateEUUDIVersion}
            title={!hasEUUDIChanges ? "No changes to save"
                : editChangesRegulation ? "The linked Basic UDI-DI has a different regulation than this version, save it as a new version"
                : `Save the changes in ${euUdiVersionInfo.VersionLabel}`}
            className="action-button save-button"
            style={{ ...euUdiSaveButtonStyle, color: "#1a73e8", backgroundColor: "#ffffff", ...(!canUpdateEUUDIVersion ? disabledSaveStyle : {}) }}
        >
            Update this version
        </button>
        <button
            onClick={() => saveEUUDIs(true)}
            disabled={!hasEUUDIChanges}
            title={!hasEUUDIChanges ? "No changes to save" : "Keep this version unchanged and save the changes as a new version"}
            className="action-button save-button"
            style={{ ...euUdiSaveButtonStyle, ...(!hasEUUDIChanges ? disabledSaveStyle : {}) }}
        >
            Save as new version
        </button>
    </> : (
        <button
            onClick={() => saveEUUDIs(false)}
            disabled={!hasEUUDIChanges}
            title={!hasEUUDIChanges ? "No changes to save" : ""}
            className="action-button save-button"
            style={{ ...euUdiSaveButtonStyle, ...(!hasEUUDIChanges ? disabledSaveStyle : {}) }}
        >
            Save
        </button>
    );


	return <>

        {/* TABS */}
        {/* TABS */}
        {/* TABS */}

		<div
            style={{
                display: "flex",
                width: "100%",
                flexDirection: "row",
                marginBottom: "15px"
            }}
        >

            <div
                style={{
                    cursor: "pointer",
                    paddingBottom: "3px",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                        ...(visibleDetailsTab == "us"
                        ? { borderBottom: "2px solid #00abff" }
                        : { borderBottom: "2px solid #f0f0f0" })
                }}
                onClick={() => changeTab("us")}
            >
                <img src="/media/35278/united_states.png" alt="" width="20" height="auto" style={{ marginRight: "5px", marginBottom: "2px" }} /> US
            </div>



            <div
                style={{
                    cursor: "pointer",
                    paddingBottom: "3px",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                        ...(visibleDetailsTab == "eu"
                        ? { borderBottom: "2px solid #00abff" }
                        : { borderBottom: "2px solid #f0f0f0" })
                }}
                onClick={() => changeTab("eu")}
            >
                <img src="/media/35267/european_union.png" alt="" width="20" height="auto" style={{ marginRight: "5px", marginBottom: "2px" }} /> EU
            </div>

                                
            <div style={{ flex: 1, borderBottom: "2px solid #f0f0f0" }}></div>

        </div>


        {/* CONTENT */}
        {/* CONTENT */}
        {/* CONTENT */}


        <div className="product-udis-screen" style={{marginBottom: "50px"}}>

            {visibleDetailsTab == "us" && <>

                {isLoadingUSUDIs ? <div><LoadingSpinnerImage /></div> :

                    <>

                        <div>
                            {AppContext.userRole == "Admin" ? <>


                                <div id="buttom-bar" style={{ height: "65px", }}>

                                    {/* FORMS BUTTONS */}

                                    <div className="action-button-bar">

                                        {AppContext.userRole == "Admin" ? <>


                                            <div className="action-button-bar-left"></div>

                                            <div className="action-button-bar-center">

                                            
                                                {isShowingUSUDIErrorMessage ? 
                                                        <span className="save-message-error">
                                                            There was a problem saving the information
                                                        </span>
                                                : null}


                                            </div>

                                            <div className="action-button-bar-right">


                                                {!isEditUSUDIMode ?
                                                    <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "start", justifyContent: "end" }} >

                                                        {isShowingUSUDISaveMessage ? 
                                                                <span className="save-message-shown">
                                                                    Save successful!
                                                                </span>
                                                            : null}

                                                        

                                                    
                                                        <div className="view-task-field" style={{ textAlign: "right" }}>

                                                            <span onClick={() => changeUSUDIEditMode(!isEditUSUDIMode)} value="EDIT" style={{ borderRadius: "20px" }} className="edit-rims-edit-button">
                                                                <i className="fas fa-edit g-pr-8"></i>Edit US UDIs
                                                            </span>
                                                        </div>

                                                    </div>

                                                    :

                                                    <>
                                                        {!isSavingUSUDIs ? (
                                                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "right", marginTop: "10px" }}>

                                                                {error ?
                                                                    <div
                                                                        style={{
                                                                            marginRight: "15px",
                                                                            color: "#f05936",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            fontSize: "13px"
                                                                        }}
                                                                    >
                                                                        Please correct the errors below before saving{/*{error}*/}
                                                                    </div>
                                                                : null}



                                                                <button
                                                                    onClick={() => cancelUSUDIEdit()}
                                                                    className="action-button save-button"
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        padding: "6px 14px",
                                                                        cursor: "pointer",
                                                                        transition: "all 0.2s ease-in-out",
                                                                        whiteSpace: "nowrap",
                                                                        outline: "none",
                                                                        color: "#5f6368",
                                                                        border: "1px solid #dee2e6",
                                                                        backgroundColor: "#ffffff"
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>

                                                                <button
                                                                    onClick={saveUSUDIs}
                                                                    className="action-button save-button"
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        padding: "6px 14px",
                                                                        cursor: "pointer",
                                                                        transition: "all 0.2s ease-in-out",
                                                                        whiteSpace: "nowrap",
                                                                        outline: "none",
                                                                        color: "#ffffff",
                                                                        border: "1px solid #1a73e8",
                                                                        backgroundColor: "#1a73e8",
                                                                    }}
                                                                >
                                                                    Save
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <LoadingSpinnerImage />
                                                            </>
                                                        )}

                                                        <span
                                                            className={`${isShowingUSUDISaveMessage
                                                                ? "save-message-shown"
                                                                : "save-message-hidden"
                                                                }`}
                                                            onTransitionEnd={() => setIsShowingUSUDISaveMessage(false)}
                                                        >
                                                            Save successful!
                                                        </span>

                                                        

                                                    </>

                                                }

                                            </div>

                                        </> : null}

                                    </div>

                                </div>



                            </> : null}
                        </div>


                        <CatalogNumber key={`field_us_1_${uSUDIversion}`} AppContext={AppContext} />
                        <IssuingAgency key={`field_us_2_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} forceIsEditing={isEditUSUDIMode} />
                        <PrimaryDINumber key={`field_us_3_${uSUDIversion}`} AppContext={AppContext} />
                        <DeviceCount key={`field_us_4_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} forceIsEditing={isEditUSUDIMode} />
                        <UnitOfUseDiNumber key={`field_us_5_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} auxFieldName={"DeviceCount"} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_6_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Labeler DUNS Number"} fieldName={"LabelerDUNSNumber"} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_7_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Brand Name"} fieldName={"BrandName"} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_8_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Version or Model"} fieldName={"VersionOrModel"} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_9_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Device Description"} fieldName={"DeviceDescription"} forceIsEditing={isEditUSUDIMode} />
                        <UDIDateField key={`field_us_10_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"DI Record Publish Date"} fieldName={"DIRecordPublishDate"} forceIsEditing={isEditUSUDIMode} />
                        <UDIDateField key={`field_us_11_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Commercial Distribution End Date"} fieldName={"CommercialDistributionEndDate"} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_12_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Device Subject to Direct Marking (DM), but Excepted"} fieldName={"DeviceSubjectToDirectMarkingDMButExcepted"} allowEmpty={true} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_13_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"DM DI Different From Primary DI"} fieldName={"DM_DI_DifferentFromPrimaryDI"} allowEmpty={true} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_14_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"DM DI Number"} fieldName={"DM_DI_Number"} string={true} min={6} max={23} forceIsEditing={isEditUSUDIMode} />
                        <SecondaryIssuingAgency key={`field_us_15_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_16_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Secondary DI Number"} fieldName={"SecondaryDINumber"} string={true} min={6} max={23} forceIsEditing={isEditUSUDIMode} />
                        <PreviousIssuingAgency key={`field_us_17_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_18_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Previous DI Number"} fieldName={"PreviousDINumber"} string={true} min={6} max={23} forceIsEditing={isEditUSUDIMode} />
                        <ProductPackageDI key={`field_us_19_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} initialData={USUDI.PackageDI} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_20_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Customer Contact Phone"} fieldName={"CustomerContactPhone"} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_21_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Customer Contact Email"} fieldName={"CustomerContactEmail"} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_22_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Human Cell, Tissue or Cellular or Tissue-Based Product (HCT/P"} fieldName={"HumanCellTissueOrCellularOrTissueBasedProduct"} allowEmpty={false} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_23_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Kit"} fieldName={"Kit"} allowEmpty={false} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_24_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Combination Product"} fieldName={"CombinationProduct"} allowEmpty={false} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_25_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Device Exempt From Premarket Submission"} fieldName={"DeviceExemptFromPremarketSubmission"} allowEmpty={true} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_26_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"FDA Premarket Submission Number"} fieldName={"FDAPremarketSubmissionNumber"} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_27_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Supplement Number"} fieldName={"SupplementNumber"} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_28_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Product Code"} fieldName={"ProductCode"} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_29_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"FDA Listing Number"} fieldName={"FDAListingNumber"} forceIsEditing={isEditUSUDIMode} />
                        <UDITextField key={`field_us_30_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"GMDN Code"} fieldName={"Code"} numeric={true} min={0} max={99999} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_31_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"For Single Use"} fieldName={"ForSingleUse"} allowEmpty={true} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_32_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Lot Or Batch Number"} fieldName={"LotOrBatchNumber"} allowEmpty={true} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_33_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Manufacturing Date"} fieldName={"ManufacturingDate"} allowEmpty={true} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_34_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Serial Number"} fieldName={"SerialNumber"} allowEmpty={true} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_35_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Expiration Date"} fieldName={"ExpirationDate"} allowEmpty={true} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_36_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Donation Identification Number"} fieldName={"DonationIdentificationNumber"} allowEmpty={true} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_37_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Device required to be labeled as containing natural rubber latex or dry natural rubber (21 CFR 801.437)"} fieldName={"DeviceRequiredToBeLabeledAsContainingNaturalRubberLatexOrDryNaturalRubber"} allowEmpty={true} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_38_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Device labeled as \"Not made with natural rubber latex\""} fieldName={"DeviceLabeledAsNotMadeWithNaturalRubberLatex"} allowEmpty={false} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_39_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Prescription Use (Rx)"} fieldName={"PrescriptionUseRx"} allowEmpty={false} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_40_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Over the Counter (OTC)"} fieldName={"OverTheCounter"} allowEmpty={false} forceIsEditing={isEditUSUDIMode} />
                        <WhatMRISafetyInformationDoesTheLabelingContain key={`field_us_41_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} forceIsEditing={isEditUSUDIMode} />
                        <ClinicallyRelevantSizes key={`field_us_42_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} initialData={USUDI.ClinicallyRelevantSizes} forceIsEditing={isEditUSUDIMode} />
                        <StorageAndHandling key={`field_us_43_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} initialData={USUDI.StorageAndHandling} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_44_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Device Packaged As Sterile"} fieldName={"DevicePackagedAsSterile"} allowEmpty={true} forceIsEditing={isEditUSUDIMode} />
                        <GenericYesNo key={`field_us_45_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} fieldTitle={"Requires Sterilization Prior To Use"} fieldName={"RequiresSterilizationPriorToUse"} allowEmpty={true} forceIsEditing={isEditUSUDIMode} />
                        <SterilizationMethod key={`field_us_46_${uSUDIversion}`} UDI={USUDI} setUDI={setUSUDI} forceIsEditing={isEditUSUDIMode} />



                        <div>
                            {AppContext.userRole == "Admin" ? <>


                                <div id="buttom-bar" style={{ height: "65px", }}>

                                    {/* FORMS BUTTONS */}

                                    <div className="action-button-bar">

                                        {AppContext.userRole == "Admin" ? <>


                                            <div className="action-button-bar-left"></div>

                                            <div className="action-button-bar-center">

                                                {isShowingUSUDIErrorMessage ? 
                                                    <span className="save-message-error">
                                                        There was a problem saving the information
                                                    </span>
                                                : null}

                                            </div>

                                            <div className="action-button-bar-right">


                                                {!isEditUSUDIMode ?

                                                    null

                                                    :

                                                    <>
                                                        {!isSavingUSUDIs ? (
                                                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "right", marginTop: "10px" }}>

                                                                {error ?
                                                                    <div
                                                                        style={{
                                                                            marginRight: "15px",
                                                                            color: "#f05936",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            fontSize: "13px"
                                                                        }}
                                                                    >
                                                                        Please correct the errors below before saving{/*{error}*/}
                                                                    </div>
                                                                : null}

                                                                

                                                                <button
                                                                    onClick={() => cancelUSUDIEdit()}
                                                                    className="action-button save-button"
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        padding: "6px 14px",
                                                                        cursor: "pointer",
                                                                        transition: "all 0.2s ease-in-out",
                                                                        whiteSpace: "nowrap",
                                                                        outline: "none",
                                                                        color: "#5f6368",
                                                                        border: "1px solid #dee2e6",
                                                                        backgroundColor: "#ffffff"
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>

                                                                <button
                                                                    onClick={saveUSUDIs}
                                                                    className="action-button save-button"
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        padding: "6px 14px",
                                                                        cursor: "pointer",
                                                                        transition: "all 0.2s ease-in-out",
                                                                        whiteSpace: "nowrap",
                                                                        outline: "none",
                                                                        color: "#ffffff",
                                                                        border: "1px solid #1a73e8",
                                                                        backgroundColor: "#1a73e8",
                                                                    }}
                                                                >
                                                                    Save
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <LoadingSpinnerImage />
                                                            </>
                                                        )}
                                                        <span
                                                            className={`${isShowingUSUDISaveMessage
                                                                ? "save-message-shown"
                                                                : "save-message-hidden"
                                                                }`}
                                                            onTransitionEnd={() => setIsShowingUSUDISaveMessage(false)}
                                                        >
                                                            Save successful!
                                                        </span>

                                                        

                                                    </>

                                                }

                                            </div>

                                        </> : null}

                                    </div>

                                </div>



                            </> : null}
                        </div>



                    </>

                }



            </>}













            {visibleDetailsTab == "eu" && <>

                {isLoadingEUUDIs ? <div><LoadingSpinnerImage /></div> :

                    <>

                        {euUdiVersions.length > 0 ?
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                                <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>Version</span>
                                <select
                                    className="form-field-text"
                                    style={{ width: "auto", minWidth: "300px" }}
                                    value={euUdiVersionInfo?.EUUDIId ?? ""}
                                    disabled={isEditEUUDIMode}
                                    title={isEditEUUDIMode ? "Save or cancel your changes to switch version" : ""}
                                    onChange={(e) => getEUUDIs(e.target.value)}
                                >
                                    {euUdiVersions.map(v => (
                                        <option key={v.Id} value={v.Id}>{v.Label}{v.IsCurrent ? " (Current)" : ""}</option>
                                    ))}
                                </select>
                                {euUdiVersionInfo && formatEUUDIVersionCreation(euUdiVersionInfo.CreatedOn, euUdiVersionInfo.CreatedByName) ?
                                    <span style={{ color: "#6c757d", fontSize: "13px" }}>
                                        Created {formatEUUDIVersionCreation(euUdiVersionInfo.CreatedOn, euUdiVersionInfo.CreatedByName)}
                                    </span>
                                : null}
                            </div>
                        : null}

                        <div>
                            {AppContext.userRole == "Admin" ? <>



                                <div id="buttom-bar" style={{ height: "65px", }}>

                                    {/* FORMS BUTTONS */}

                                    <div className="action-button-bar">

                                        {AppContext.userRole == "Admin" ? <>

                                            <div className="action-button-bar-left"></div>
                                            <div className="action-button-bar-center">
                                                
                                                {isShowingEUUDIErrorMessage ? 
                                                        <span className="save-message-error">
                                                            {euUdiSaveErrorMessage ?? "There was an error saving the information"}
                                                        </span>
                                                : null}

                                            </div>
                                            <div className="action-button-bar-right">

                                                {!isEditEUUDIMode ?
                                                    <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "start", justifyContent: "end" }} >

                                                        {isShowingEUUDISaveMessage ? 
                                                                <span className="save-message-shown">
                                                                    Save successful!
                                                                </span>
                                                            : null}


                                                        {hasEUUDIs ?
                                                            <div className="view-task-field" style={{ textAlign: "right", marginRight: "10px" }}>
                                                                <span onClick={openEudamedXmlPopup} style={{ borderRadius: "20px" }} className="btn u-btn-outline-primary g-font-size-14 g-pl-16 g-pr-16 g-pt-6 g-pb-6">
                                                                    <i className="fas fa-file-code g-pr-8"></i>EUDAMED XML
                                                                </span>
                                                            </div>
                                                        : null}

                                                        <div className="view-task-field" style={{ textAlign: "right" }}>
                                                            <span onClick={() => changeEUUDIEditMode(!isEditEUUDIMode)} value="EDIT" style={{ borderRadius: "20px" }} className="edit-rims-edit-button">
                                                                <i className="fas fa-edit g-pr-8"></i>Edit EU UDIs
                                                            </span>
                                                        </div>

                                                    </div>

                                                    :

                                                    <>

                                                        {!isSavingEUUDIs ? (
                                                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "right", marginTop: "10px" }}>

                                                                {error ?
                                                                    <div
                                                                        style={{
                                                                            marginRight: "15px",
                                                                            color: "#f05936",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            fontSize: "13px"
                                                                        }}
                                                                    >
                                                                        Please correct the errors below before saving{/*{error}*/}
                                                                    </div>
                                                                : null}

                                                                

                                                                <button
                                                                    onClick={() => cancelEUUDIEdit()}
                                                                    className="action-button save-button"
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        padding: "6px 14px",
                                                                        cursor: "pointer",
                                                                        transition: "all 0.2s ease-in-out",
                                                                        whiteSpace: "nowrap",
                                                                        outline: "none",
                                                                        color: "#5f6368",
                                                                        border: "1px solid #dee2e6",
                                                                        backgroundColor: "#ffffff"
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>

                                                                {renderEUUDISaveButtons()}
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <LoadingSpinnerImage />
                                                            </>
                                                        )}
                                                        <span
                                                            className={`${isShowingEUUDISaveMessage
                                                                ? "save-message-shown"
                                                                : "save-message-hidden"
                                                                }`}
                                                            onTransitionEnd={() => setIsShowingEUUDISaveMessage(false)}
                                                        >
                                                            Save successful!
                                                        </span>

                                                    </>

                                                }


                                            </div>

                                        </> : null}

                                    </div>

                                </div>



                            </> : null}
                        </div>

                        <MasterUDIDICode key={`field_eu_1_${eUUDIversion}`} AppContext={AppContext} mandatory={true} />
                        <IssuingEntityMasterUDIDI key={`field_eu_2_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} forceIsEditing={isEditEUUDIMode} />
                        <UDITextField key={`field_eu_3_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"Secondary UDI-DI Code / (Master) UDI - DI Code"} fieldName={"SecondaryMaster_UDI_DI_Code"} forceIsEditing={isEditEUUDIMode} />
                        <IssuingEntitySecondaryMasterUDIDI key={`field_eu_4_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} forceIsEditing={isEditEUUDIMode} />
                        <UDITextField key={`field_eu_5_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"Unit of Use DI Code"} fieldName={"UnitOfUseDICode"} forceIsEditing={isEditEUUDIMode} />
                        <IssuingEntityUnitofUseDI key={`field_eu_6_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} forceIsEditing={isEditEUUDIMode} />
                        <UDITextField key={`field_eu_7_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"Direct Marking UDI-DI Code"} fieldName={"DirectMarkingUDI_DI_Code"} forceIsEditing={isEditEUUDIMode} />
                        <IssuingEntityDirectMarkingDI key={`field_eu_8_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} forceIsEditing={isEditEUUDIMode} />
                        <BasicUDIDIIdentifier key={`field_eu_9_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} forceIsEditing={isEditEUUDIMode} />
                        <ProductionIdentifiers key={`field_eu_10_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.TypeOfUDI_PI} forceIsEditing={isEditEUUDIMode} />
                        <EMDNNomenclature key={`field_eu_11_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.NomenclatureCode} forceIsEditing={isEditEUUDIMode} />
                        <UDITextField key={`field_eu_12_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"Quantity of Device"} fieldName={"QuantityOfDevice"} mandatory={true} numeric={true} min={1} max={99999} forceIsEditing={isEditEUUDIMode} />
                        <GenericYesNo key={`field_eu_13_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"Containing Latex"} fieldName={"ContainingLatex"} allowEmpty={true} forceIsEditing={isEditEUUDIMode} />
                        <GenericYesNo key={`field_eu_15_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"New Device"} fieldName={"NewDevice"} allowEmpty={true} forceIsEditing={isEditEUUDIMode} />
                        <ReferenceCatalogueNumber key={`field_eu_16_${eUUDIversion}`} AppContext={AppContext} />
                        <GenericYesNo key={`field_eu_17_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"Reprocessed single use device"} fieldName={"ReprocessedSingleUseDevice"} allowEmpty={true} forceIsEditing={isEditEUUDIMode} />
                        <GenericYesNo key={`field_eu_18_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"Labelled as Single Use"} fieldName={"LabelledAsSingleUse"} allowEmpty={true} mandatory={true} forceIsEditing={isEditEUUDIMode} />
                        <MaximumNumberOfReuses key={`field_eu_14_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} forceIsEditing={isEditEUUDIMode} auxFieldName={"LabelledAsSingleUse"} />
                        <GenericYesNo key={`field_eu_19_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"Device Labelled Sterile"} fieldName={"DeviceLabelledSterile"} allowEmpty={true} mandatory={true} forceIsEditing={isEditEUUDIMode} />
                        <GenericYesNo key={`field_eu_20_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"Need for Sterilisation Before Use"} fieldName={"NeedForSterilisationBeforeUse"} allowEmpty={true} mandatory={true} forceIsEditing={isEditEUUDIMode} />
                        <UDITextField key={`field_eu_21_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"URL for Additional Information"} fieldName={"URLForAdditionalInformation"} forceIsEditing={isEditEUUDIMode} />
                        <AdditionalProductDescription key={`field_eu_22_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.AdditionalProductDescription} forceIsEditing={isEditEUUDIMode} />
                        <GenericYesNo key={`field_eu_23_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"Trade Name Applicable"} fieldName={"TradeNameApplicable"} allowEmpty={true} mandatory={true} forceIsEditing={isEditEUUDIMode} />
                        <TradeName key={`field_eu_24_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.TradeName} forceIsEditing={isEditEUUDIMode} />
                        <UDIDIDeviceStatus key={`field_eu_25_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} forceIsEditing={isEditEUUDIMode} />
                        <NonMedicalDeviceType key={`field_eu_26_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.IntendedPurposeOtherThanMedicalAnnexXVI} forceIsEditing={isEditEUUDIMode} />
                        <CMRSubstanceList key={`field_eu_27_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.ListOfCMRSubstancesAssociatedToDevice} forceIsEditing={isEditEUUDIMode} />
                        <EndocrineSubstancesList key={`field_eu_28_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.ListOfEndocrineSubstancesAssociatedToDevice} forceIsEditing={isEditEUUDIMode} />
                        <MedicinalProductSubstancesList key={`field_eu_29_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.ListOfMedicinalProductSubstancesAssociatedToTheDevice} forceIsEditing={isEditEUUDIMode} />
                        <EUStorageAndHandlingConditionsList key={`field_eu_30_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.ListOfStorageAndHandlingConditions} forceIsEditing={isEditEUUDIMode} validationTrigger={storageValidationTrigger} />
                        <CriticalWarningsOrContraindications key={`field_eu_31_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.ListOfCriticalWarningsOrContraindications} forceIsEditing={isEditEUUDIMode} validationTrigger={warningsValidationTrigger} />
                        <ClinicalSizes key={`field_eu_32_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.ClinicalSizes} forceIsEditing={isEditEUUDIMode} />
                        <ProductDesignerLink key={`field_eu_33_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.NaturalOrLegalPersonWhoManufacturedAndDesingedTheDevice} forceIsEditing={isEditEUUDIMode} validationTrigger={productDesignerValidationTrigger} />
                        <MemberStateOfThePlacingOnTheEUMarketOfTheDevice key={`field_eu_34_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} forceIsEditing={isEditEUUDIMode} />
                        <MarketInfo key={`field_eu_35_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.MemberStatesWhereDeviceIsOrIsToBeMadeAvailable} forceIsEditing={isEditEUUDIMode} />
                        <UDITextField key={`field_eu_36_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} fieldTitle={"Related Legacy Device"} fieldName={"RelatedLegacyDevice"} forceIsEditing={isEditEUUDIMode} />
                        <RelationshipType key={`field_eu_37_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} forceIsEditing={isEditEUUDIMode} auxFieldName={"RelatedLegacyDevice"} />
                        <ContainerPack key={`field_eu_38_${eUUDIversion}`} UDI={EUUDI} setUDI={setEUUDI} initialData={EUUDI.ContainerPackagesRelatedTo_UDI_DI} forceIsEditing={isEditEUUDIMode} />



                        <div>
                            {AppContext.userRole == "Admin" ? <>



                                <div id="buttom-bar" style={{ height: "65px", }}>

                                    {/* FORMS BUTTONS */}

                                    <div className="action-button-bar">

                                        {AppContext.userRole == "Admin" ? <>

                                            <div className="action-button-bar-left"></div>
                                            <div className="action-button-bar-center">
                                                
                                                {isShowingEUUDIErrorMessage ? 
                                                        <span className="save-message-error">
                                                            {euUdiSaveErrorMessage ?? "There was an error saving the information"}
                                                        </span>
                                                    : null}

                                            </div>
                                            <div className="action-button-bar-right">

                                                {!isEditEUUDIMode ?

                                                    null

                                                    :

                                                    <>

                                                        {!isSavingEUUDIs ? (
                                                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "right", marginTop: "10px" }}>

                                                                {error ?
                                                                    <div
                                                                        style={{
                                                                            marginRight: "15px",
                                                                            color: "#f05936",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            fontSize: "13px"
                                                                        }}
                                                                    >
                                                                        Please correct the errors below before saving{/*{error}*/}
                                                                    </div>
                                                                : null}

                                                                
                                                                <button
                                                                    onClick={() => cancelEUUDIEdit()}
                                                                    className="action-button save-button"
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        padding: "6px 14px",
                                                                        cursor: "pointer",
                                                                        transition: "all 0.2s ease-in-out",
                                                                        whiteSpace: "nowrap",
                                                                        outline: "none",
                                                                        color: "#5f6368",
                                                                        border: "1px solid #dee2e6",
                                                                        backgroundColor: "#ffffff"
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>

                                                                {renderEUUDISaveButtons()}
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <LoadingSpinnerImage />
                                                            </>
                                                        )}
                                                        <span
                                                            className={`${isShowingEUUDISaveMessage
                                                                ? "save-message-shown"
                                                                : "save-message-hidden"
                                                                }`}
                                                            onTransitionEnd={() => setIsShowingEUUDISaveMessage(false)}
                                                        >
                                                            Save successful!
                                                        </span>

                                                    </>

                                                }


                                            </div>

                                        </> : null}

                                    </div>

                                </div>



                            </> : null}
                        </div>


                    </>
                }


            </>}

                                                                
        </div>




        {/* Unsaved UDI Changes Warning Popup */}
        {/* Unsaved UDI Changes Warning Popup */}

        {AppContext.isShowingUDIUnsavedChangesWarning ?

            <div
                style={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000
                }}
            >
                <div
                    style={{
                        width: "360px",
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                        boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                        padding: "20px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px"
                    }}
                >
                    {/* Title */}
                    <div style={{ fontSize: "1rem", fontWeight: 600 }}>
                        Unsaved Changes
                    </div>

                    {/* Message */}
                    <div style={{ fontSize: "0.9rem", color: "#555", lineHeight: "1.4" }}>
                        There are unsaved changes in the UDI screen.
                        <br />
                        Please save or cancel your changes before leaving.
                    </div>

                    {/* Actions */}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                        <button
                            onClick={() => AppContext.setIsShowingUDIUnsavedChangesWarning(false)}
                            style={{
                                padding: "8px 16px",
                                backgroundColor: "#1976d2",
                                color: "#fff",
                                border: "none",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "0.85rem",
                                fontWeight: 500,
                                transition: "background-color 0.2s ease"
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#155fa0"}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#1976d2"}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            : null}



            {/* EXPORT EUDAMED XML POPUP */}
            {showEudamedXmlPopup && (
                <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.35)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ background: "white", borderRadius: "15px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", padding: "25px", width: "520px", maxWidth: "90vw" }}>

                        <div style={{ display: "flex", flexDirection: "row" }}>
                            <div style={{ flex: 1 }}>
                                <h3>Export EUDAMED XML</h3>
                            </div>
                            <div>
                                <button className="close-button" onClick={() => setShowEudamedXmlPopup(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>

                        <p>
                            Select which EUDAMED XML to generate for this product's EU (Master) UDI-DI:
                        </p>
                        <select className="form-field-text" style={{ width: "100%" }}
                            value={selectedEudamedService}
                            onChange={(e) => setSelectedEudamedService(e.target.value)}
                        >
                            {eudamedXmlServices.map((service) => (
                                <option key={service.value} value={service.value}>{service.label}</option>
                            ))}
                        </select>

                        {eudamedXmlError ? <><br /><span className="form-field-error-message" style={{ whiteSpace: "pre-line" }}>{eudamedXmlError}</span></> : null}
                        {eudamedXmlWarning ? <><br /><span style={{ whiteSpace: "pre-line", color: "#6c757d" }}>Warning: {eudamedXmlWarning}</span></> : null}

                        {eudamedSendSuccess ? <><br /><span style={{ color: "#28a745" }}>{eudamedSendSuccess}</span></> : null}

                        <EudamedPlaygroundNotice />

                        <div style={{ marginTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>

                            <div style={{ display: "flex", gap: "10px" }}>
                                {isGeneratingEudamedXml || isSendingEudamedXml ? (
                                    <LoadingSpinnerImage />
                                ) : (
                                    <>
                                        <button
                                            className="action-button save-button"
                                            onClick={downloadEudamedXml}
                                        >
                                            Download XML
                                        </button>

                                        <button
                                            className="action-button save-button"
                                            disabled={!eudamedEnabled || eudamedXmlServices.find(s => s.value === selectedEudamedService)?.extension === ".zip"}
                                            title={!eudamedEnabled
                                                ? "EUDAMED submission is not enabled for your company"
                                                : eudamedXmlServices.find(s => s.value === selectedEudamedService)?.extension === ".zip"
                                                    ? "This service produces a zip - download it and submit the file individually"
                                                    : "Submit this XML directly to EUDAMED"}
                                            style={eudamedEnabled && eudamedXmlServices.find(s => s.value === selectedEudamedService)?.extension !== ".zip" ? {} : { opacity: 0.5, cursor: "not-allowed" }}
                                            onClick={sendEudamedXmlToEudamed}
                                        >
                                            Send to EUDAMED
                                        </button>
                                    </>
                                )}
                            </div>

                            <button
                                className="action-button cancel-button"
                                onClick={() => setShowEudamedXmlPopup(false)}
                            >
                                Cancel
                            </button>

                        </div>

                    </div>
                </div>
            )}


	</>

}


export default ProductUDIs;